<template>
  <div>
    <div class="tw-flex tw-justify-between">
      <div class="tw-text-xl tw-font-light tw-my-auto">{{ capitalize(organization.customerLabelSingular) }} Tips</div>
      <div class="tw-flex">
        <base-button type="text" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
        <export-button
          class="tw-ml-5"
          size="sm"
          type="primary"
          label="Export"
          :filename="`Tips.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/reports/tip/export`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
    </div>
    <filter-display v-if="showFilters === true" class="tw-my-3" :filters="filters" />
    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
      <base-card class="tw-mb-3 tw-mt-7">
        <div class="tw-text-gray-500 tw-text-sm">
          Total Tips Collected
        </div>
        <base-skeleton-loader v-if="isLoading === true" class="tw-w-20 tw-h-6 tw-mt-1" />
        <Money-Format v-else class="tw-text-lg tw-font-normal tw-text-gray-800" :value="sum" :locale="locale" :currency-code="currency" subunits-value />
      </base-card>
      <aggregate-list
        class="tw-max-h-60"
        :class="{ 'tw-overflow-y-scroll': dailySummary.length > 6 }"
        :aggregates="dailySummary"
        :isLoading="isLoading"
        labelKey="summaryLabel"
        idKey="summaryId"
        valueKey="tipAmountInCents"
        title="Location Summary"
        format="currency"
      >
      </aggregate-list>
    </div>
    <div class="tw-mt-6">{{ page.total }} tips</div>
    <base-table :loading="isLoading" :fields="fields" :items="tips" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" :loadingRows="5" class="tw-mt-2">
      <template #date="{item: tip}">
        <div class="tw-whitespace-nowrap">{{ formatDate(tip.createdDate) }}</div>
      </template>
      <template #amount="{item: tip}">
        <Money-Format :value="tip.tipAmountInCents" :locale="locale" :currency-code="tip.currency" subunits-value />
      </template>
      <template #status="{item: tip}">
        <base-badge size="sm" :color="statusColor(tip.status)" class="tw-whitespace-nowrap">{{ capitalize(tip.status) }}</base-badge>
      </template>
      <template #location="{item: tip}">
        <div class="tw-whitespace-nowrap">{{ tip.businessLocationName }}</div>
      </template>
      <template #description="{item: tip}">
        <div class="tw-whitespace-nowrap">
          <base-button type="text" @click="view(tip.userId)">{{ tip.memberName }}</base-button
          >: {{ tip.description }}
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-16" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <report-filter-slideover
      ref="filterSlideOver"
      :business-id="businessId"
      date-filter-label="Tip Date"
      :show-programs-filter="false"
      :show-program-groups-filter="false"
      :show-prescription-status-filter="false"
      :show-plan-status-filter="false"
      :show-plan-groups-filter="false"
      :show-plans-filter="false"
      @filter-updated="applyFilter"
    />
  </div>
</template>

<script>
import ReportFilterSlideover from "@/views/business/reports/components/ReportFilterSlideover";
import AggregateList from "@/views/business/reports/components/AggregateList.vue";
import FilterDisplay from "@/views/business/reports/components/FilterDisplay";
import { FilterIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize } from "lodash";
import ExportButton from "@/components/ExportButton.vue";
import MoneyFormat from "vue-money-format";

const TAKE_KEY = "predictile_tips_report_take";

export default {
  name: "BusinessMemberTipsReport",
  components: {
    ReportFilterSlideover,
    FilterDisplay,
    FilterIcon,
    ExportButton,
    MoneyFormat,
    AggregateList,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      tips: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      sum: 0,
      currency: "usd",
      dailySummary: [],
      isLoading: false,
      filters: {
        startDate: null,
        endDate: null,
        locationsFilter: [],
        plansFilter: [],
        planGroupsFilter: [],
      },
      sortKey: "date",
      sortDesc: true,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    locale() {
      return window.navigator.language;
    },
    showFilters() {
      if (this.filters.startDate !== null || this.filters.endDate !== null || this.filters.locationsFilter.length > 0) return true;
      else return false;
    },
    fields() {
      let columns = [];
      columns.push({ title: "Date", key: "date", isSortable: true });
      columns.push({ title: "Amount", key: "amount" });
      columns.push({ title: "Status", key: "status", isSortable: true });
      columns.push({ title: "Description", key: "description" });
      columns.push({ title: "Location", key: "location", isSortable: true });

      return columns;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item["businessLocationId"];
          })
        : [];

      return {
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        businessLocationIds: locationFilterStrings,
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc === true ? "desc" : "asc",
      };
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  mounted() {
    this.$refs.filterSlideOver.dateShortcutSelected("month");
    this.$refs.filterSlideOver.applyFilter();
  },
  methods: {
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the report. Please reload the page to try again.",
        error: err,
      });
    },
    statusColor(status) {
      switch (status) {
        case "succeeded":
          return "green";
        case "failed":
          return "red";
        default:
          return "gray";
      }
    },
    capitalize,
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      this.filters = filters;
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/reports/tip/search?skip=${this.page.skip}&take=${this.page.take}`;

      this.$axios
        .post(url, this.requestPayload)
        .then((res) => {
          this.tips = res.data.page.items;
          this.page.total = res.data.page.total;
          this.sum = res.data.sum;
          this.currency = res.data.currency;
          this.dailySummary = res.data.dailySummary;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load tips",
            message: "We had a problem retrieving the list of tips. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      return new moment.utc(date).local().format("M/D/YYYY h:mm a");
    },
    view(userId) {
      window.open(`/businesses/${this.businessId}/members/${userId}/history`, "_blank");
    },
  },
};
</script>
